import type {
  SeedProvider,
  SeedRunAiSummary,
  SeedRunAuthCheck,
  SeedRunDetail,
  SeedRunDetailResponse,
  SeedRunEngagement,
  SeedRunFacetsResponse,
  SeedRunIspResult,
  SeedRunList,
  SeedRunMetrics,
  SeedRunMetricsResponse,
  SeedRunReport,
  SeedRunListEnvelope,
  SeedRunRecipient,
  SeedRunRecipientList,
  SeedRunRecipientListEnvelope,
  SeedRunRecipientResponse,
  SeedRunRecipientStatsResponse,
  SeedRunReportSummary,
  SeedRunReportSummaryResponse,
  SeedRunRow,
  SeedRunRowResponse,
  SeedRunSparkPost,
  SeedRunSparkPostResponse,
  SeedRunStatsResponse,
  SeedSparkPostIspKey,
} from '../types';

/**
 * Null stays null. A run the vendor has not reported on is not a run that scored zero, and the
 * Inbox column draws an empty value rather than `0%` because of it.
 */
function reportSummaryMapper(summary: SeedRunReportSummaryResponse | null): SeedRunReportSummary | null {
  if (!summary) return null;
  return {
    inboxCount: summary.inbox_count,
    spamCount: summary.spam_count,
    missingCount: summary.missing_count,
    seenCount: summary.seen_count,
    totalCount: summary.total_count,
  };
}

/** The six the server normalises every vendor ISP name into. Anything else is not an ISP we know. */
const PROVIDERS = new Set<string>(['Gmail', 'Yahoo', 'Outlook', 'AOL', 'iCloud', 'Other']);

/** A finite number, or null. `NaN` and `Infinity` reach a tile as "NaN%", so they are not numbers. */
function readNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/** A counter the panel adds up. Absent reads as zero, which is what a missing bucket means. */
function readCount(value: unknown): number {
  return readNumber(value) ?? 0;
}

function readText(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

/** An absent pair is `0 pass · 0 fail`, which the panel draws as "Failing" for nobody. */
function authCheckMapper(value: unknown): SeedRunAuthCheck {
  const check = (value ?? {}) as Record<string, unknown>;
  return { pass: readCount(check.pass), fail: readCount(check.fail) };
}

function ispResultMapper(value: unknown): SeedRunIspResult {
  const row = (value ?? {}) as Record<string, unknown>;
  const placements = row.folder_placements as Record<string, unknown> | null | undefined;

  return {
    // The server normalises the vendor's display names into the six the seed list derives from an
    // address. A name outside that set is drawn as "Other" rather than as a seventh ISP the rest
    // of this area cannot badge.
    isp: (PROVIDERS.has(row.isp as string) ? row.isp : 'Other') as SeedProvider,
    total: readCount(row.total),
    inbox: readCount(row.inbox),
    spam: readCount(row.spam),
    missing: readCount(row.missing),
    // Only Gmail reports tabs. Null is "no tabs", which is not the same as Primary 0 · Promotions 0.
    folderPlacements:
      placements && typeof placements === 'object'
        ? { primary: readCount(placements.primary), promotions: readCount(placements.promotions) }
        : null,
  };
}

/**
 * Engagement, or null when the run carries none.
 *
 * Null stays null on EVERY counter. The server stores a counter the vendor did not report as
 * null, and `readCount` would turn it back into a zero the tab draws as a measurement. A block
 * where no counter is a number is not engagement, and the tab shows its empty state instead.
 */
function engagementMapper(value: unknown): SeedRunEngagement | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;

  const engagement = value as Record<string, unknown>;

  const mapped: SeedRunEngagement = {
    impressions: readNumber(engagement.impressions),
    uniqueOpens: readNumber(engagement.unique_opens),
    averageReadSeconds: readNumber(engagement.average_read_seconds),
    shortRead: readNumber(engagement.short_read),
    mediumRead: readNumber(engagement.medium_read),
    longRead: readNumber(engagement.long_read),
    desktop: readNumber(engagement.desktop),
    mobile: readNumber(engagement.mobile),
  };

  return Object.values(mapped).some((counter) => counter !== null) ? mapped : null;
}

/**
 * The placement report, or null when the column holds nothing readable.
 *
 * The server types this column `unknown` and passes it through without validating it, so the
 * narrowing happens here. The FOUR placement counters are what decide, the same four the collect
 * service's adapter requires before it stores anything: a payload missing any of them was not
 * written by it. Reading a partial one would print the missing counter as a zero — "0 % inbox" for
 * a run nobody measured that way — and degrading to null puts "not available yet" on the tab,
 * which is true.
 *
 * Everything below those four stays tolerant on purpose: an absent `seen_count`, auth pair or ISP
 * row makes a thinner panel, and no rule on the page reads one of them as a verdict.
 */
export function runReportMapper(value: unknown): SeedRunReport | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;

  const report = value as Record<string, unknown>;
  const totalCount = readNumber(report.total_count);
  const inboxCount = readNumber(report.inbox_count);
  const spamCount = readNumber(report.spam_count);
  const missingCount = readNumber(report.missing_count);
  if (totalCount === null || inboxCount === null || spamCount === null || missingCount === null) return null;

  return {
    totalCount,
    seenCount: readCount(report.seen_count),
    inboxCount,
    spamCount,
    missingCount,
    spf: authCheckMapper(report.spf),
    dkim: authCheckMapper(report.dkim),
    dmarc: authCheckMapper(report.dmarc),
    firstSeenAt: readText(report.first_seen_at),
    lastSeenAt: readText(report.last_seen_at),
    ispResults: (Array.isArray(report.isp_results) ? report.isp_results : []).map(ispResultMapper),
    engagement: engagementMapper(report.engagement),
  };
}

/**
 * The metrics block. A missing block reads as "no vendor measures this run" with no window,
 * which is what an empty `sources` list means on the wire.
 */
export function runMetricsMapper(value: SeedRunMetricsResponse | null | undefined): SeedRunMetrics {
  return {
    state: value?.state ?? 'not_applicable',
    windowClosesAt: value?.window_closes_at ?? null,
    sources: (value?.sources ?? []).map((source) => ({
      vendor: source.vendor,
      state: source.state,
      syncedAt: source.synced_at ?? null,
      lastError: source.last_error ?? null,
    })),
  };
}

/** eDataSource's ISP keys, in the order the Placement tab lists them, with the badge each one wears. */
const SPARKPOST_ISPS: [SeedSparkPostIspKey, SeedProvider][] = [
  ['gmail', 'Gmail'],
  ['outlook', 'Outlook'],
  ['yahoo', 'Yahoo'],
  ['icloud', 'iCloud'],
  ['aol', 'AOL'],
  ['other', 'Other'],
];

/**
 * The SparkPost Email Analyst numbers, or null when the run has none.
 *
 * The three headline numbers decide, like the four counters of `runReportMapper`: a block missing
 * one of them would draw "0 %" for a rate nobody measured. An ISP key the report does not carry
 * has no row, rather than a row of zeros the vendor never sent.
 */
export function runSparkPostMapper(value: SeedRunSparkPostResponse | null | undefined): SeedRunSparkPost | null {
  if (!value || typeof value !== 'object') return null;

  const panelVolume = readNumber(value.panel_volume);
  const inboxRate = readNumber(value.inbox_rate);
  const spamRate = readNumber(value.spam_rate);
  if (panelVolume === null || inboxRate === null || spamRate === null) return null;

  const breakdown = (value.isp_breakdown ?? {}) as Record<string, unknown>;

  return {
    panelVolume,
    inboxRate,
    spamRate,
    syncedAt: readText(value.synced_at),
    ispResults: SPARKPOST_ISPS.flatMap(([key, isp]) => {
      const counts = breakdown[key] as Record<string, unknown> | null | undefined;
      if (!counts || typeof counts !== 'object') return [];
      return [
        { isp, inbox: readCount(counts.inbox), spam: readCount(counts.spam), missing: readCount(counts.missing) },
      ];
    }),
  };
}

/** The vendor's summary, or null. An empty text is no summary, not a summary that says nothing. */
export function runAiSummaryMapper(value: SeedRunDetailResponse['ai_summary'] | undefined): SeedRunAiSummary | null {
  if (!value) return null;

  const text = readText(value.text) ?? '';
  const isGenerating = Boolean(value.is_generating);
  if (!text && !isGenerating) return null;

  return { text, isGenerating };
}

/** One run as the list shows it. The detail is this plus the setup and the snapshot. */
export function runRowMapper(row: SeedRunRowResponse): SeedRunRow {
  return {
    id: row.id,
    type: row.type,
    campaignId: row.campaign_id,
    campaignName: row.campaign_name,
    campaignSlug: row.campaign_slug ?? null,
    status: row.status,
    metricsState: row.metrics_state,
    trackingHeaderName: row.tracking_header_name,
    trackingHeaderValue: row.tracking_header_value,
    groupIds: row.group_ids ?? [],
    recipientCount: row.recipient_count,
    looseCount: row.loose_count,
    startedAt: row.started_at,
    // The name is the run's own snapshot, so it still reads after the admin has left Pray.
    startedBy: row.started_by,
    // Null until a send path reports: a run nobody has answered for is not a run with zero.
    suppressedCount: row.suppressed_count ?? null,
    // `null` stays `null`: a run with no failure is not a run that failed with an empty reason.
    failureCode: row.failure_code ?? null,
    failureReason: row.failure_reason ?? null,
    reportSummary: reportSummaryMapper(row.report_summary ?? null),
    lastRefreshedAt: row.last_refreshed_at ?? null,
  };
}

/**
 * The run, in the UI's casing.
 *
 * `report_summary` and `report` both cross, and they are not the same thing: the summary is the
 * five mirrored counters every list row carries, the report is the JSON blob only the detail
 * reads. The Placement tab draws the report; the Inbox column of the list draws the summary.
 */
export function runMapper(row: SeedRunDetailResponse): SeedRunDetail {
  return {
    ...runRowMapper(row),
    artistId: row.artist_id ?? null,
    occurrenceDate: row.occurrence_date ?? null,
    catalogSelections: (row.catalog_selections ?? []).map((selection) => ({
      slug: selection.slug,
      catalogId: selection.catalog_id,
      key: selection.key,
      // The Key names a run written before the server owned the rule: those rows
      // carry the client's old `title`, which could be a whole catalog row.
      label: selection.label || selection.key,
    })),
    replacements: row.replacements ?? {},
    customHeaders: row.custom_headers ?? [],
    headers: row.headers ?? {},
    seedIds: row.seed_ids ?? [],
    ispMix: row.isp_mix,
    sparkpostHeaderName: row.sparkpost_header_name ?? null,
    dispatchedAt: row.dispatched_at ?? null,
    sentAt: row.sent_at ?? null,
    dispatchedCount: row.dispatched_count,
    acceptedCount: row.accepted_count ?? null,
    metrics: runMetricsMapper(row.metrics),
    sparkpost: runSparkPostMapper(row.sparkpost),
    // Null on the campaign types, where the aggregate counters are the answer.
    sendOutcomes: row.send_outcomes ?? null,
    sent: {
      from: row.sent?.from ?? null,
      subject: row.sent?.subject ?? null,
      preview: row.sent?.preview ?? null,
      html: row.sent?.html ?? null,
      // A transactional run stores these instead of a body, and "What was sent" shows them.
      templateId: row.sent?.template_id ?? null,
      dynamicTemplateData: row.sent?.dynamic_template_data ?? null,
    },
    report: runReportMapper(row.report),
    aiSummary: runAiSummaryMapper(row.ai_summary),
  };
}

export function runListMapper(envelope: SeedRunListEnvelope): SeedRunList {
  return {
    runs: (envelope.data ?? []).map(runRowMapper),
    total: envelope.total,
    limit: envelope.limit,
    offset: envelope.offset,
    lastItemIdentifier: envelope.last_item_identifier ?? null,
    stats: runStatsMapper(envelope.stats),
    facets: runFacetsMapper(envelope.facets),
  };
}

export function runStatsMapper(stats: SeedRunStatsResponse) {
  return {
    runCount: stats.run_count,
    inProgressCount: stats.in_progress_count,
    // Null, never 0: no run with a report means no rate, and a 0 % reads as total failure.
    meanInboxRate: stats.mean_inbox_rate ?? null,
  };
}

export function runFacetsMapper(facets: SeedRunFacetsResponse) {
  return { type: facets.type, status: facets.status, metricsState: facets.metrics_state };
}

export function runRecipientMapper(row: SeedRunRecipientResponse): SeedRunRecipient {
  return {
    email: row.email,
    provider: row.provider,
    // The uuid survives a soft delete; it is null only once the seed row is gone for good.
    seedId: row.seed_id ?? null,
    stillInSeedList: row.still_in_seed_list,
    viaGroups: row.via_groups ?? [],
  };
}

export function runRecipientStatsMapper(stats: SeedRunRecipientStatsResponse) {
  return { total: stats.total, looseCount: stats.loose_count, ispMix: stats.isp_mix };
}

export function runRecipientListMapper(envelope: SeedRunRecipientListEnvelope): SeedRunRecipientList {
  return {
    recipients: (envelope.data ?? []).map(runRecipientMapper),
    total: envelope.total,
    limit: envelope.limit,
    offset: envelope.offset,
    lastItemIdentifier: envelope.last_item_identifier ?? null,
    stats: runRecipientStatsMapper(envelope.stats),
  };
}
